import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { isCvinExpansion } from "../projectGridColumns";
import { canAccessReporting, reportingPluginEnabled } from "./reportingAccess";
import { fetchBatchProjectContractFields } from "./contractsApi";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import { useBoardTheme } from "./boardThemeStore";
import { fetchAllExpansionSegmentFinancials } from "./expansionSegmentFinancialsApi";
import {
  EXPANSION_BUDGET_GROUP_BY,
  EXPANSION_BUDGET_METRICS,
  EXPANSION_BUDGET_TIMEFRAMES,
  EXPANSION_PHASE_FLOW,
  BUDGETED_METRIC_KEY,
  COMPLETED_CONDUIT_METRIC_KEY,
  COMPLETED_FIBER_METRIC_KEY,
  COMPLETED_PROOFING_METRIC_KEY,
  aggregateExpansionBudgetChart,
  aggregateInspectorCompletedCaltransPie,
  buildExpansionBudgetReportRows,
  filterExpansionBudgetRows,
  filterInspectorCompletedFootageRows,
  filterNewFeetStartedRows,
  formatBudgetCurrency,
  formatReportFootage,
  formatReportMetric,
  formatReportPercent,
  isInspectorCompletedFootageMetric,
  isNewFeetMetric,
  isProgressMetric,
  isTotalPermittedMetric,
  permittedFootageProjectPercent,
  sumExpansionProjectFootage,
  summarizeExpansionBudget,
  summarizeInspectorCompletedByCaltrans,
  toExpansionBudgetGridRows,
} from "./expansionBudgetReportData";
import {
  aggregatePermittedByMonth,
  aggregatePermittedPieChart,
  buildExpansionPermitEligibleDetailRows,
  buildExpansionPermittedDetailRows,
  filterPermittedDetailRows,
  summarizePermittedDetail,
  summarizeRemainingPermittedDetail,
  toPermittedMonthlyGridRows,
} from "./expansionPermittedReportData";
import { BudgetBarChart, BudgetPieChart } from "./reportingCharts";
import { CommercialReportingPanel } from "./CommercialReportingPanel";
import "./reportingPage.css";

ModuleRegistry.registerModules([AllCommunityModule]);

class ReportingViewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <p className="reporting-status reporting-status--error">
          Could not render this metric view. Try another metric or reload the page.
        </p>
      );
    }
    return this.props.children;
  }
}

function segNumberComparator(a, b) {
  const na = a != null && a !== "" ? Number(a) : Number.POSITIVE_INFINITY;
  const nb = b != null && b !== "" ? Number(b) : Number.POSITIVE_INFINITY;
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });
}

function currencyValueFormatter(params) {
  return formatBudgetCurrency(params.value);
}

function footageValueFormatter(params) {
  return formatReportFootage(params.value);
}

function percentValueFormatter(params) {
  return formatReportPercent(params.value);
}

const EXPANSION_BUDGET_GRID_COLUMNS = [
  {
    field: "segNumber",
    headerName: "Seg#",
    sort: "asc",
    comparator: segNumberComparator,
    width: 90,
    pinned: "left",
  },
  { field: "woBase", headerName: "WO Base", width: 110 },
  { field: "owner", headerName: "Owner", width: 130 },
  { field: "contractors", headerName: "Contractor(s)", width: 160 },
  { field: "phase", headerName: "Phase", width: 120 },
  { field: "status", headerName: "Status", width: 120 },
  {
    field: "totalFeet",
    headerName: "Total Feet",
    width: 110,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "completedFootage",
    headerName: "Completed",
    width: 110,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "completedConduitFootage",
    headerName: "Construction Done",
    width: 140,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "caltransConduitFootage",
    headerName: "Construction Caltrans",
    width: 150,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "nonCaltransConduitFootage",
    headerName: "Construction Non-CT",
    width: 150,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "completedFiberFootage",
    headerName: "Fiber Done",
    width: 110,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "caltransFiberFootage",
    headerName: "Fiber Caltrans",
    width: 120,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "nonCaltransFiberFootage",
    headerName: "Fiber Non-CT",
    width: 120,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "completedProofingFootage",
    headerName: "Proofing Done",
    width: 120,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "caltransProofingFootage",
    headerName: "Proofing Caltrans",
    width: 140,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "nonCaltransProofingFootage",
    headerName: "Proofing Non-CT",
    width: 140,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "awardedFootage",
    headerName: "Awarded Ft",
    width: 110,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "awardedPercent",
    headerName: "% Awarded",
    width: 120,
    valueFormatter: percentValueFormatter,
    type: "numericColumn",
  },
  {
    field: "remainingFeetToAward",
    headerName: "Remaining to Award",
    width: 145,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "remainingFootageToComplete",
    headerName: "Remaining Ft",
    width: 115,
    valueFormatter: footageValueFormatter,
    type: "numericColumn",
  },
  {
    field: "totalBudget",
    headerName: "Total Budget",
    width: 130,
    valueFormatter: currencyValueFormatter,
    type: "numericColumn",
  },
  {
    field: "budgeted",
    headerName: "Budgeted",
    width: 130,
    valueFormatter: currencyValueFormatter,
    type: "numericColumn",
  },
  {
    field: "awardedBudget",
    headerName: "Awarded Budget",
    width: 140,
    valueFormatter: currencyValueFormatter,
    type: "numericColumn",
  },
  {
    field: "remainingBudget",
    headerName: "Remaining Budget",
    width: 140,
    valueFormatter: currencyValueFormatter,
    type: "numericColumn",
  },
  {
    field: "expectedFinalCost",
    headerName: "Expected Final",
    width: 130,
    valueFormatter: currencyValueFormatter,
    type: "numericColumn",
  },
  {
    field: "variance",
    headerName: "Variance",
    width: 120,
    valueFormatter: currencyValueFormatter,
    type: "numericColumn",
  },
];

const PERMITTED_MONTHLY_GRID_COLUMNS = [
  {
    field: "monthLabel",
    headerName: "Month",
    sort: "desc",
    comparator: (_a, _b, nodeA, nodeB) => {
      const keyA = nodeA?.data?.monthKey ?? "";
      const keyB = nodeB?.data?.monthKey ?? "";
      return String(keyB).localeCompare(String(keyA));
    },
    width: 130,
    pinned: "left",
  },
  {
    field: "caltransFootage",
    headerName: "Caltrans (ft)",
    width: 130,
    type: "numericColumn",
    valueFormatter: footageValueFormatter,
  },
  {
    field: "nonCaltransFootage",
    headerName: "Non-Caltrans (ft)",
    width: 150,
    type: "numericColumn",
    valueFormatter: footageValueFormatter,
  },
  {
    field: "totalFootage",
    headerName: "Total (ft)",
    width: 120,
    type: "numericColumn",
    valueFormatter: footageValueFormatter,
  },
  {
    field: "segmentCount",
    headerName: "Subsegments",
    width: 120,
    type: "numericColumn",
  },
];

function PhaseFilter({ selected, onChange }) {
  const toggle = (phase) => {
    const set = new Set(selected);
    if (set.has(phase)) set.delete(phase);
    else set.add(phase);
    onChange([...set]);
  };

  return (
    <div className="reporting-phase-list" role="group" aria-label="Filter by phase">
      {EXPANSION_PHASE_FLOW.map((phase) => (
        <button
          key={phase}
          type="button"
          className={`reporting-phase-chip${selected.includes(phase) ? " is-selected" : ""}`}
          onClick={() => toggle(phase)}
        >
          {phase}
        </button>
      ))}
    </div>
  );
}

function ExpansionBudgetPanel({ apiUrl, getAuthHeaders }) {
  const { agGridTheme } = useBoardTheme();
  const contractsPluginOn = isPluginEnabled(PLUGIN_IDS.CONTRACTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metricKey, setMetricKey] = useState(EXPANSION_BUDGET_METRICS[0].key);
  const [groupByKey, setGroupByKey] = useState(EXPANSION_BUDGET_GROUP_BY[0].key);
  const [timeframe, setTimeframe] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [expansionProjects, setExpansionProjects] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = getAuthHeaders?.() || {};
      const { data: projects } = await axios.get(`${apiUrl}/projects`, { headers });
      const expansionRows = (Array.isArray(projects) ? projects : []).filter(isCvinExpansion);
      setExpansionProjects(expansionRows);
      const financialsMap = await fetchAllExpansionSegmentFinancials();
      let contractFieldsByProjectId = null;
      if (contractsPluginOn) {
        const projectIds = expansionRows.map((row) => row.id).filter(Boolean);
        contractFieldsByProjectId = await fetchBatchProjectContractFields(projectIds, {
          apiUrl,
          getAuthHeaders,
        });
      }
      setReportRows(
        buildExpansionBudgetReportRows(projects, financialsMap, contractFieldsByProjectId)
      );
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load expansion budget data.");
      setReportRows([]);
      setExpansionProjects([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, contractsPluginOn, getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isPermittedMetric = isTotalPermittedMetric(metricKey);
  const isNewFeet = isNewFeetMetric(metricKey);
  const isInspectorCompleted = isInspectorCompletedFootageMetric(metricKey);
  const usesCaltransSplit = isPermittedMetric || isInspectorCompleted;

  const permitEligibleRows = useMemo(
    () => buildExpansionPermitEligibleDetailRows(expansionProjects),
    [expansionProjects]
  );

  const permittedDetailRows = useMemo(
    () => buildExpansionPermittedDetailRows(expansionProjects),
    [expansionProjects]
  );

  const permittedFilters = useMemo(
    () => ({
      phases: selectedPhases,
      timeframe,
      customFrom,
      customTo,
    }),
    [selectedPhases, timeframe, customFrom, customTo]
  );

  const filteredPermittedRows = useMemo(
    () => filterPermittedDetailRows(permittedDetailRows, permittedFilters),
    [permittedDetailRows, permittedFilters]
  );

  const remainingPermittedSummary = useMemo(() => {
    // Remaining is not timeframe-scoped: it is all eligible footage still waiting on a permit.
    const phaseSet = new Set((selectedPhases || []).map((p) => String(p).trim()).filter(Boolean));
    const scoped =
      phaseSet.size === 0
        ? permitEligibleRows
        : permitEligibleRows.filter((row) => phaseSet.has(row.phase));
    return summarizeRemainingPermittedDetail(scoped);
  }, [permitEligibleRows, selectedPhases]);

  const permittedMonthlyRows = useMemo(
    () => aggregatePermittedByMonth(filteredPermittedRows),
    [filteredPermittedRows]
  );

  const permittedGridRows = useMemo(
    () => toPermittedMonthlyGridRows(permittedMonthlyRows),
    [permittedMonthlyRows]
  );

  const permittedChartData = useMemo(
    () => aggregatePermittedPieChart(filteredPermittedRows),
    [filteredPermittedRows]
  );

  const permittedSummary = useMemo(
    () => summarizePermittedDetail(filteredPermittedRows),
    [filteredPermittedRows]
  );

  const filteredRows = useMemo(() => {
    if (isNewFeet) {
      return filterNewFeetStartedRows(reportRows, expansionProjects, {
        phases: selectedPhases,
        timeframe,
        customFrom,
        customTo,
      });
    }
    if (isInspectorCompleted) {
      return filterInspectorCompletedFootageRows(reportRows, expansionProjects, {
        phases: selectedPhases,
        timeframe,
        customFrom,
        customTo,
        metricKey,
      });
    }
    return filterExpansionBudgetRows(reportRows, {
      phases: selectedPhases,
      timeframe,
      customFrom,
      customTo,
    });
  }, [
    isNewFeet,
    isInspectorCompleted,
    metricKey,
    reportRows,
    expansionProjects,
    selectedPhases,
    timeframe,
    customFrom,
    customTo,
  ]);

  const chartData = useMemo(() => {
    if (isPermittedMetric) return permittedChartData;
    if (isInspectorCompleted) {
      return aggregateInspectorCompletedCaltransPie(filteredRows, metricKey);
    }
    return aggregateExpansionBudgetChart(filteredRows, { metricKey, groupByKey });
  }, [
    isPermittedMetric,
    isInspectorCompleted,
    permittedChartData,
    filteredRows,
    metricKey,
    groupByKey,
  ]);

  const gridRows = useMemo(
    () => (isPermittedMetric ? permittedGridRows : toExpansionBudgetGridRows(filteredRows)),
    [isPermittedMetric, permittedGridRows, filteredRows]
  );

  const gridColumns = useMemo(() => {
    if (isPermittedMetric) return PERMITTED_MONTHLY_GRID_COLUMNS;
    const showConduit = metricKey === COMPLETED_CONDUIT_METRIC_KEY;
    const showFiber = metricKey === COMPLETED_FIBER_METRIC_KEY;
    const showProofing = metricKey === COMPLETED_PROOFING_METRIC_KEY;
    const showBudgeted = metricKey === BUDGETED_METRIC_KEY;
    const showAwarded = metricKey === "Awarded Footage";
    const showAwardedExtras = showAwarded || metricKey === "Total Budget" || showBudgeted;

    // Awarded Footage gets a focused table: footage + % awarded (no Contractor(s) clutter).
    if (showAwarded) {
      const awardedFields = new Set([
        "segnumber",
        "wobase",
        "phase",
        "status",
        "totalfeet",
        "awardedfootage",
        "awardedpercent",
        "remainingfeettoaward",
      ]);
      return EXPANSION_BUDGET_GRID_COLUMNS.filter((col) =>
        awardedFields.has(String(col.field || "").toLowerCase())
      );
    }

    return EXPANSION_BUDGET_GRID_COLUMNS.filter((col) => {
      const field = String(col.field || "").toLowerCase();
      if (field.includes("conduit")) return showConduit;
      if (field.includes("fiber")) return showFiber;
      if (field.includes("proofing")) return showProofing;
      if (field === "completedfootage") return isInspectorCompleted;
      if (field === "budgeted") return showBudgeted || metricKey === "Total Budget";
      if (field === "awardedbudget") return showBudgeted;
      if (field === "remainingbudget" || field === "expectedfinalcost" || field === "variance") {
        return metricKey === "Total Budget" || showBudgeted;
      }
      if (field === "totalbudget") return metricKey === "Total Budget" || showBudgeted;
      if (field === "awardedfootage" || field === "awardedpercent") return showAwardedExtras;
      if (field === "remainingfeettoaward") return false;
      if (field === "remainingfootagetocomplete") return isInspectorCompleted;
      return true;
    });
  }, [isPermittedMetric, isInspectorCompleted, metricKey]);

  const totalProjectFootage = useMemo(
    () => sumExpansionProjectFootage(reportRows, { phases: selectedPhases }),
    [reportRows, selectedPhases]
  );

  const summary = useMemo(() => {
    if (isPermittedMetric) {
      const eligibleTotal = remainingPermittedSummary.eligibleFootage;
      return {
        segmentCount: permittedSummary.segmentCount,
        segmentsWithData: permittedSummary.segmentCount,
        total: permittedSummary.totalFootage,
        caltransFootage: permittedSummary.caltransFootage,
        nonCaltransFootage: permittedSummary.nonCaltransFootage,
        remainingFootage: remainingPermittedSummary.remainingFootage,
        remainingCount: remainingPermittedSummary.remainingCount,
        totalProjectFootage: eligibleTotal || totalProjectFootage,
        projectFootagePct: permittedFootageProjectPercent(
          permittedSummary.totalFootage,
          eligibleTotal || totalProjectFootage
        ),
      };
    }
    if (isInspectorCompleted) {
      return summarizeInspectorCompletedByCaltrans(filteredRows, metricKey);
    }
    return summarizeExpansionBudget(filteredRows, metricKey);
  }, [
    isPermittedMetric,
    isInspectorCompleted,
    permittedSummary,
    remainingPermittedSummary,
    filteredRows,
    metricKey,
    totalProjectFootage,
  ]);

  const metricLabel =
    EXPANSION_BUDGET_METRICS.find((m) => m.key === metricKey)?.label || metricKey;
  const groupByLabel =
    EXPANSION_BUDGET_GROUP_BY.find((g) => g.key === groupByKey)?.label || groupByKey;
  const formatMetric = useCallback((value) => formatReportMetric(value, metricKey), [metricKey]);
  const showProgressPercent = isProgressMetric(metricKey);

  return (
    <div className="reporting-panel">
      <div className="reporting-filters">
        <div className="reporting-filter">
          <label htmlFor="reporting-metric">Metric</label>
          <select
            id="reporting-metric"
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
          >
            {EXPANSION_BUDGET_METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="reporting-filter">
          <label htmlFor="reporting-group-by">Group by (chart axis)</label>
          <select
            id="reporting-group-by"
            value={usesCaltransSplit ? "caltrans" : groupByKey}
            onChange={(e) => setGroupByKey(e.target.value)}
            disabled={usesCaltransSplit}
          >
            {usesCaltransSplit ? (
              <option value="caltrans">Caltrans / Non-Caltrans</option>
            ) : (
              EXPANSION_BUDGET_GROUP_BY.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="reporting-filter">
          <label htmlFor="reporting-timeframe">
            {isPermittedMetric
              ? "Timeframe (permit received)"
              : isNewFeet
                ? "Timeframe (first inspector report)"
                : isInspectorCompleted
                  ? "Timeframe (inspector report date)"
                  : "Timeframe"}
          </label>
          <select
            id="reporting-timeframe"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          >
            {EXPANSION_BUDGET_TIMEFRAMES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        {timeframe === "custom" ? (
          <>
            <div className="reporting-filter">
              <label htmlFor="reporting-from">From</label>
              <input
                id="reporting-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div className="reporting-filter">
              <label htmlFor="reporting-to">To</label>
              <input
                id="reporting-to"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </>
        ) : null}
        <div className="reporting-filter" style={{ gridColumn: "1 / -1" }}>
          <label>Phases</label>
          <PhaseFilter selected={selectedPhases} onChange={setSelectedPhases} />
        </div>
      </div>

      {loading ? <p className="reporting-status">Loading expansion budget data…</p> : null}
      {error ? <p className="reporting-status reporting-status--error">{error}</p> : null}

      {!loading && !error ? (
        <>
          <div className="reporting-kpis">
            <div className="reporting-kpi">
              <div className="reporting-kpi-label">
                {isPermittedMetric ? "Subsegments" : "Segments"}
              </div>
              <div className="reporting-kpi-value">{summary.segmentCount}</div>
            </div>
            {usesCaltransSplit ? (
              <>
                <div className="reporting-kpi">
                  <div className="reporting-kpi-label">
                    {isPermittedMetric ? "Caltrans permitted" : "Caltrans"}
                  </div>
                  <div className="reporting-kpi-value">
                    {formatReportFootage(summary.caltransFootage)}
                  </div>
                </div>
                <div className="reporting-kpi">
                  <div className="reporting-kpi-label">
                    {isPermittedMetric ? "Non-Caltrans permitted" : "Non-Caltrans"}
                  </div>
                  <div className="reporting-kpi-value">
                    {formatReportFootage(summary.nonCaltransFootage)}
                  </div>
                </div>
              </>
            ) : (
              <div className="reporting-kpi">
                <div className="reporting-kpi-label">With {metricLabel}</div>
                <div className="reporting-kpi-value">{summary.segmentsWithData}</div>
              </div>
            )}
            <div className="reporting-kpi">
              <div className="reporting-kpi-label">Total {metricLabel}</div>
              <div className="reporting-kpi-value">{formatMetric(summary.total)}</div>
              {isPermittedMetric && summary.projectFootagePct != null ? (
                <div className="reporting-kpi-sub">
                  {formatReportPercent(summary.projectFootagePct)} of{" "}
                  {formatReportFootage(summary.totalProjectFootage)} eligible
                </div>
              ) : null}
            </div>
            {metricKey === "Awarded Footage" && summary.projectFootagePct != null ? (
              <div className="reporting-kpi">
                <div className="reporting-kpi-label">Overall % Awarded</div>
                <div className="reporting-kpi-value">
                  {formatReportPercent(summary.projectFootagePct)}
                </div>
                <div className="reporting-kpi-sub">
                  {formatReportFootage(summary.total)} of{" "}
                  {formatReportFootage(summary.totalProjectFootage)} project footage
                </div>
              </div>
            ) : null}
            {isPermittedMetric ? (
              <div className="reporting-kpi">
                <div className="reporting-kpi-label">Remaining to permit</div>
                <div className="reporting-kpi-value">
                  {formatReportFootage(summary.remainingFootage)}
                </div>
                <div className="reporting-kpi-sub">
                  {summary.remainingCount} subsegment
                  {summary.remainingCount === 1 ? "" : "s"} without a received permit
                </div>
              </div>
            ) : null}
          </div>

          <ReportingViewErrorBoundary resetKey={metricKey}>
            <div className={`reporting-charts${usesCaltransSplit ? " reporting-charts--single" : ""}`}>
              <section className="reporting-chart-card">
                <h3 className="reporting-chart-title">
                  {usesCaltransSplit
                    ? `${metricLabel} — Caltrans vs Non-Caltrans`
                    : `${metricLabel} by ${groupByLabel}`}
                </h3>
                <BudgetPieChart data={chartData} formatValue={formatMetric} />
              </section>
              {!usesCaltransSplit ? (
                <section className="reporting-chart-card">
                  <h3 className="reporting-chart-title">{metricLabel} comparison</h3>
                  <BudgetBarChart
                    data={chartData}
                    formatValue={formatMetric}
                    showProgressPercent={showProgressPercent}
                  />
                </section>
              ) : null}
            </div>

            {isPermittedMetric ? (
              <h3 className="reporting-section-title">Permitted footage by month</h3>
            ) : null}

            <div className="reporting-grid-wrap">
              {/* Remount on metric change so AgGrid does not crash when column/row shapes swap. */}
              <AgGridReact
                key={`expansion-grid-${metricKey}`}
                theme={agGridTheme}
                rowData={gridRows}
                columnDefs={gridColumns}
                getRowId={(params) => {
                  const id = params?.data?.id;
                  if (id != null && String(id).trim() !== "") return String(id);
                  if (params?.data?.monthKey) return `month:${params.data.monthKey}`;
                  if (params?.data?.woBase) return `wo:${params.data.woBase}`;
                  return `row:${params?.node?.id ?? Math.random()}`;
                }}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 80,
                }}
                domLayout="autoHeight"
                suppressCellFocus
              />
            </div>
          </ReportingViewErrorBoundary>
        </>
      ) : null}
    </div>
  );
}

export function ReportingPage({ apiUrl, getAuthHeaders }) {
  const { agGridTheme } = useBoardTheme();
  const [activePane, setActivePane] = useState("expansion");

  return (
    <div className="reporting-shell">
      <div className="reporting-page">
        <header className="reporting-page-header">
          <h1 className="reporting-page-title">Reporting</h1>
          <p className="reporting-page-lead">
            Budget and project analytics for expansion and commercial portfolios. Commercial
            reporting summarizes MRC and project counts by workflow phase, with average days per
            phase from change history. Expansion budget reporting uses segment financial fields.
          </p>
        </header>

        <div className="reporting-tabs" role="tablist" aria-label="Reporting portfolio">
          <button
            type="button"
            role="tab"
            aria-selected={activePane === "commercial"}
            className={`reporting-tab${activePane === "commercial" ? " is-active" : ""}`}
            onClick={() => setActivePane("commercial")}
          >
            Commercial
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePane === "expansion"}
            className={`reporting-tab${activePane === "expansion" ? " is-active" : ""}`}
            onClick={() => setActivePane("expansion")}
          >
            Expansion
          </button>
        </div>

        {activePane === "expansion" ? (
          <ExpansionBudgetPanel apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />
        ) : (
          <CommercialReportingPanel gridTheme={agGridTheme} />
        )}
      </div>
    </div>
  );
}

export function ReportingGate({ apiUrl, getAuthHeaders }) {
  if (!reportingPluginEnabled()) {
    return <Navigate to="/" replace />;
  }
  if (!canAccessReporting()) {
    return <Navigate to="/" replace />;
  }
  return <ReportingPage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}

export default ReportingGate;
